import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { pitchLabel } from "@/lib/pitchLabel";
import { Clock, X, Repeat, Download, Filter, ArrowLeftRight, Check, Ban, MessageSquare, Sparkles, Radio, Pencil, AlertTriangle } from "lucide-react";
import { FABadge, isFAImported } from "@/components/app/FABadge";
import { BookingDetailDialog } from "@/components/calendar/BookingDetailDialog";
import { SecretaryNoticeDialog } from "@/components/calendar/SecretaryNoticeDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerUK } from "@/components/app/DatePickerUK";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Link, useSearchParams } from "react-router-dom";

// CSV cell escape: wrap in quotes and double any embedded quotes.
const csvCell = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function MyBookings() {
  const { user, siteSettings } = useAuth();
  const isAdmin = user?.role === "admin";
  const secretaryEnabled = siteSettings?.secretary_notices_enabled !== false;
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [swaps, setSwaps] = useState({ outgoing: [], incoming: [], admin_pending: [], history: [] });

  // Swap-request modal state
  const [swapFor, setSwapFor] = useState(null); // source booking
  const [swapTargets, setSwapTargets] = useState([]);
  const [swapTargetId, setSwapTargetId] = useState("");
  const [swapMessage, setSwapMessage] = useState("");
  const [swapScope, setSwapScope] = useState("single");
  const [secretaryNotice, setSecretaryNotice] = useState(null); // { booking, kind }
  const [swapBusy, setSwapBusy] = useState(false);

  // Offer-swap-from-clash flow: when the coach lands here via
  // `/app/bookings?swap_target=<id>` (from the New Booking dialog's "Propose swap"
  // button), we open the OfferSwap dialog pre-loaded with that target so the coach
  // only has to pick which of THEIR bookings to offer in exchange.
  const [searchParams, setSearchParams] = useSearchParams();
  const [offerSwap, setOfferSwap] = useState(null); // { target: fullBookingDoc }
  const [offerSourceId, setOfferSourceId] = useState("");
  const [offerMessage, setOfferMessage] = useState("");
  const [offerBusy, setOfferBusy] = useState(false);

  useEffect(() => {
    const tid = searchParams.get("swap_target");
    if (!tid) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${tid}`);
        if (cancelled) return;
        if (data.user_id === user?.id) {
          toast.info("That's your own booking — nothing to swap here.");
        } else {
          setOfferSwap({ target: data });
          setOfferSourceId("");
          setOfferMessage("");
        }
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load the swap target");
      } finally {
        // Clear the URL param so a refresh doesn't reopen the dialog.
        const next = new URLSearchParams(searchParams);
        next.delete("swap_target");
        setSearchParams(next, { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("swap_target"), user?.id]);

  // Deep-link support for `/app/bookings?booking_id=<id>` — used by the Admin
  // Team Clashes panel to jump straight into the BookingDetailDialog on any
  // booking (including bookings the admin doesn't own). Admins already have
  // showAllAdmin auto-enabled below so the list they land on includes the target.
  useEffect(() => {
    const bid = searchParams.get("booking_id");
    if (!bid) return;
    if (isAdmin) setShowAllAdmin(true);
    setOpenId(bid);
    const next = new URLSearchParams(searchParams);
    next.delete("booking_id");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("booking_id")]);

  const submitOfferSwap = async () => {
    if (!offerSwap || !offerSourceId) return;
    setOfferBusy(true);
    try {
      await api.post(`/bookings/${offerSourceId}/swap-request`, {
        target_booking_id: offerSwap.target.id,
        message: offerMessage,
        scope: "single",
      });
      toast.success("Swap requested — the current holder + admins have been notified");
      setOfferSwap(null); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't propose that swap");
    } finally { setOfferBusy(false); }
  };

  // "List for swap" dialog state
  const [listFor, setListFor] = useState(null);
  const [listNote, setListNote] = useState("");
  const [listBusy, setListBusy] = useState(false);

  // Booking detail modal — reused from the calendar so users get full amend + series edit + cancel
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [teamsOwned, setTeamsOwned] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [referees, setReferees] = useState([]);
  const kitLabels = { Black: "Black", White: "White", Green: "Green", Yellow: "Yellow", Blue: "Blue" };

  // Filters
  const [q, setQ] = useState("");
  const [sessionType, setSessionType] = useState("all");
  const [ageGroup, setAgeGroup] = useState("all");
  const [teamId, setTeamId] = useState("all");
  const [venueFilter, setVenueFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Admins can flip this to see EVERY booking in the club (uses `/bookings` — full
  // dataset, all users). Everyone else stays on `/bookings/mine`. Since amend/cancel
  // permission is already checked server-side via `can_manage_booking`, admins get
  // Amend + Cancel buttons on any row automatically once the detail dialog opens.
  const [showAllAdmin, setShowAllAdmin] = useState(false);

  const load = async () => {
    setLoading(true);
    const bookingsPath = isAdmin && showAllAdmin ? "/bookings" : "/bookings/mine";
    const [b, s] = await Promise.all([api.get(bookingsPath), api.get("/booking-swaps")]);
    setBookings(b.data);
    setSwaps(s.data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showAllAdmin]);
  // Teams + pitches + referees — one fetch on mount so the amend dialog can populate its dropdowns
  useEffect(() => {
    api.get("/teams?owned_only=true").then(r => setTeamsOwned(r.data || [])).catch(() => setTeamsOwned([]));
    api.get("/teams").then(r => setAllTeams(r.data || [])).catch(() => setAllTeams([]));
    api.get("/pitches").then(r => setPitches(r.data || [])).catch(() => setPitches([]));
    api.get("/referees").then(r => setReferees(r.data || [])).catch(() => setReferees([]));
  }, []);
  // Whenever a booking is opened for detail, fetch the enriched detail (with team/coach/referee/league)
  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    setDetailLoading(true);
    api.get(`/bookings/${openId}`)
      .then(r => setDetail(r.data))
      .catch(err => { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load booking"); setOpenId(null); })
      .finally(() => setDetailLoading(false));
  }, [openId]);

  const openSwapDialog = async (b) => {
    setSwapFor(b); setSwapTargetId(""); setSwapMessage(""); setSwapScope("single");
    try {
      const { data } = await api.get(`/bookings/${b.id}/swap-targets`);
      setSwapTargets(data.candidates || []);
      // Auto-highlight the smart best match (same age group / kit colour etc.)
      if (data.best_match_id) setSwapTargetId(data.best_match_id);
      if ((data.candidates || []).length === 0) toast.info("No same-day bookings owned by other coaches, and no upcoming team fixtures either.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load swap options");
      setSwapFor(null);
    }
  };
  const submitSwap = async () => {
    if (!swapFor || !swapTargetId) return;
    setSwapBusy(true);
    try {
      await api.post(`/bookings/${swapFor.id}/swap-request`, {
        target_booking_id: swapTargetId,
        message: swapMessage,
        scope: swapScope,
      });
      toast.success("Swap requested — target coach + admins have been notified");
      setSwapFor(null); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't request that swap");
    } finally { setSwapBusy(false); }
  };
  const decideSwap = async (id, action) => {
    if (action === "decline" && !window.confirm("Decline this swap request?")) return;
    if (action === "cancel"  && !window.confirm("Cancel your swap request?")) return;
    try {
      await api.post(`/booking-swaps/${id}/${action}`);
      toast.success(action === "accept" ? "Swap accepted — bookings exchanged" : `Swap ${action}d`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update that swap");
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this booking?")) return;
    try { await api.delete(`/bookings/${id}`); toast.success("Booking cancelled"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openListDialog = (b) => { setListFor(b); setListNote(""); };
  const submitList = async () => {
    if (!listFor) return;
    setListBusy(true);
    try {
      await api.post(`/bookings/${listFor.id}/swap-list`, { note: listNote });
      toast.success("Listed on the Swap Board — other coaches can now offer swaps");
      setListFor(null); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't list that booking");
    } finally { setListBusy(false); }
  };
  const unlist = async (id) => {
    if (!window.confirm("Remove this booking from the Swap Board?")) return;
    try {
      await api.post(`/bookings/${id}/swap-unlist`);
      toast.success("Removed from Swap Board");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const cancelSeries = async (seriesId) => {
    if (!window.confirm("Cancel ALL remaining bookings in this recurring series?")) return;
    try {
      const { data } = await api.post(`/bookings/series/${seriesId}/cancel`);
      toast.success(`Cancelled ${data.cancelled} booking${data.cancelled !== 1 ? "s" : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Distinct teams present in the user's own bookings — powers the team dropdown.
  // Labels use "AgeGroup · TeamName" so a coach with several squads across ages can spot the right one fast.
  const teamOptions = useMemo(() => {
    const map = new Map();
    bookings.forEach(b => {
      if (b.team_id && b.team_name) {
        const label = b.age_group ? `${b.age_group} · ${b.team_name}` : b.team_name;
        map.set(b.team_id, { label, age: b.age_group || "" });
      }
    });
    return Array.from(map.entries()).sort((a,b) => a[1].label.localeCompare(b[1].label));
  }, [bookings]);

  // Distinct age groups across the user's bookings, sorted U6..U18 then Adults, then anything else alphabetically.
  const ageOptions = useMemo(() => {
    const set = new Set();
    bookings.forEach(b => { if (b.age_group) set.add(b.age_group); });
    const arr = Array.from(set);
    const rank = (a) => {
      const m = /^U(\d+)$/.exec(a);
      if (m) return parseInt(m[1], 10);
      if (a === "Adults") return 999;
      return 1000;
    };
    return arr.sort((a,b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [bookings]);

  const filtered = useMemo(() => bookings.filter(b => {
    if (sessionType !== "all" && (b.session_type || "training") !== sessionType) return false;
    if (ageGroup !== "all" && b.age_group !== ageGroup) return false;
    if (teamId !== "all" && b.team_id !== teamId) return false;
    if (venueFilter !== "all" && pitchLabel(b) !== venueFilter) return false;
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (dateFrom && b.booking_date < dateFrom) return false;
    if (dateTo && b.booking_date > dateTo) return false;
    if (q) {
      const needle = q.toLowerCase();
      const hay = [b.pitch_name, b.team_name, b.opponent, b.notes, b.match_type, b.referee_name].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  }).sort((a, b) => (a.booking_date || "").localeCompare(b.booking_date || "") || (a.start_hour || 0) - (b.start_hour || 0) || (a.start_min || 0) - (b.start_min || 0)), [bookings, sessionType, ageGroup, teamId, venueFilter, statusFilter, dateFrom, dateTo, q]);
  const venueOptions = useMemo(() => Array.from(new Set(bookings.map(pitchLabel).filter(Boolean))).sort(), [bookings]);

  const clearFilters = () => { setQ(""); setSessionType("all"); setAgeGroup("all"); setTeamId("all"); setVenueFilter("all"); setStatusFilter("all"); setDateFrom(""); setDateTo(""); };
  const activeFilterCount = [q, sessionType!=="all"?sessionType:"", ageGroup!=="all"?ageGroup:"", teamId!=="all"?teamId:"", venueFilter!=="all"?venueFilter:"", statusFilter!=="all"?statusFilter:"", dateFrom, dateTo].filter(Boolean).length;

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error("No bookings to export with these filters"); return; }
    const headers = ["Date","Start","End","Pitch","Session","Match type","Team","Age group","Opponent","Referee","Ref fee (£)","Status","Notes","Booking ID"];
    const rows = filtered.map(b => [
      b.booking_date,
      `${String(b.start_hour).padStart(2,"0")}:${String(b.start_min||0).padStart(2,"0")}`,
      `${String(b.end_hour).padStart(2,"0")}:${String(b.end_min||0).padStart(2,"0")}`,
      b.pitch_name || "",
      b.session_type || "training",
      b.match_type || "",
      b.team_name || "",
      b.age_group || "",
      b.opponent || "",
      b.referee_name || "",
      b.expected_referee_fee != null ? Number(b.expected_referee_fee).toFixed(2) : "",
      b.status || "",
      (b.notes || "").replace(/\r?\n/g, " "),
      b.id,
    ]);
    const csv = [headers, ...rows].map(r => r.map(csvCell).join(",")).join("\n");
    // BOM ensures Excel renders £ / accented characters correctly.
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `my-bookings-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} booking${filtered.length !== 1 ? "s" : ""}`);
  };

  const today = new Date().toISOString().slice(0,10);
  const upcoming = filtered.filter(b => b.booking_date >= today && b.status === "confirmed");
  const past = filtered.filter(b => b.booking_date < today || b.status === "cancelled").reverse();

  const seriesCounts = {};
  upcoming.forEach(b => {
    if (b.recurring_series_id) {
      seriesCounts[b.recurring_series_id] = (seriesCounts[b.recurring_series_id] || 0) + 1;
    }
  });

  const listedForSwap = upcoming.filter(b => b.swap_open);

  // Bookings with an incoming pending swap — used to badge the row
  const incomingByBooking = useMemo(() => {
    const m = new Map();
    (swaps.incoming || []).forEach(s => m.set(s.target_booking_id, s));
    return m;
  }, [swaps.incoming]);
  // Bookings with an outgoing pending swap the user raised
  const outgoingByBooking = useMemo(() => {
    const m = new Map();
    (swaps.outgoing || []).forEach(s => m.set(s.initiator_booking_id, s));
    return m;
  }, [swaps.outgoing]);
  const pendingCount = (swaps.incoming?.length || 0) + (swaps.outgoing?.length || 0) + (isAdmin ? (swaps.admin_pending?.length || 0) : 0);

  // Row-level checklist — surfaces missing bits (kit, opponent, referee, notes) as small pills
  // so coaches can spot gaps without opening every dialog. Games get all four checks; trainings
  // only get kit + notes (they don't have opponents or referees).
  const rowChecklist = (b) => {
    const isGame = b.session_type === "game";
    const items = [
      { key: "kit", label: "Kit", ok: !!b.team_color },
      isGame && { key: "opponent", label: "Opponent", ok: !!(b.opponent && b.opponent.trim()) },
      isGame && { key: "ref", label: "Ref", ok: !!b.referee_name },
      { key: "notes", label: "Notes", ok: !!(b.notes && b.notes.trim()) },
    ].filter(Boolean);
    return items;
  };

  const Row = ({ b, canCancel }) => (
    <div className="card-tactical rounded-md p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`booking-row-${b.id}`}>
      <div className="flex items-center gap-5 flex-wrap">
        <div className="w-14 text-center">
          <div className="font-display font-black text-3xl text-[#83B68B] leading-none">{format(parseISO(b.booking_date), "dd")}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mt-1">{format(parseISO(b.booking_date), "MMM")}</div>
        </div>
        <div>
          <div className="font-display font-bold text-xl flex items-center gap-2">
            {pitchLabel(b)}
            {b.recurring_series_id && <Repeat className="w-3.5 h-3.5 text-[#83B68B]" title="Part of a recurring series" />}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs opacity-70 mt-1">
            <span data-testid={`booking-time-${b.id}`} className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {String(b.start_hour).padStart(2,"0")}:{String(b.start_min || 0).padStart(2,"0")} — {String(b.end_hour).padStart(2,"0")}:{String(b.end_min || 0).padStart(2,"0")}</span>
            <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded ${b.session_type === "game" ? "bg-[#83B68B]/20 text-[#83B68B]" : "bg-muted opacity-80"}`}>
              {b.session_type || "training"}{b.match_type ? ` · ${b.match_type}` : ""}
            </span>
            {isFAImported(b) && <FABadge testId={`fa-badge-${b.id}`} />}
            {b.opponent && <span>vs {b.opponent}</span>}
            {b.team_name ? <span>· {b.team_name}</span> : (b.age_group && !b.team_id) ? <span data-testid={`booking-agegroup-${b.id}`}>· {b.age_group} year group</span> : null}
            {Array.isArray(b.shared_team_names) && b.shared_team_names.length > 0 && (
              <span data-testid={`booking-shared-${b.id}`} className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-[#83B68B]/15 border border-[#83B68B]/40 text-[#83B68B]" title={`Shared training — pitch shared with: ${b.shared_team_names.join(", ")}`}>
                {b.shared_with_you ? "Shared with your team" : `Sharing with ${b.shared_team_names.join(" · ")}`}
              </span>
            )}
            {b.referee_name && (
              <span data-testid={`booking-ref-${b.id}`}>
                · Ref: {b.referee_name}
                {b.referee_is_qualified != null && (
                  <span className="ml-1 text-[9px] tracking-widest uppercase opacity-70">({b.referee_is_qualified ? "qualified" : "unqualified"})</span>
                )}
              </span>
            )}
            {b.expected_referee_fee != null && (
              <span data-testid={`booking-fee-${b.id}`} className="text-[10px] tracking-widest uppercase bg-[#83B68B]/20 text-[#83B68B] px-1.5 py-0.5 rounded">
                Ref fee £{Number(b.expected_referee_fee).toFixed(2)}
              </span>
            )}
            {b.status === "cancelled" && <span className="text-red-500">· Cancelled</span>}
            {b.fa_change_pending && (
              <span
                data-testid={`booking-fa-change-${b.id}`}
                title={`FA Full-Time now says ${b.fa_change_pending.new_date} ${b.fa_change_pending.new_kickoff} — open to confirm or keep`}
                className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-400/50 text-sky-200 inline-flex items-center gap-1"
              >
                <AlertTriangle className="w-3 h-3" /> FA moved — confirm
              </span>
            )}
            {b.confirmation_status === "awaiting" && (
              <span
                data-testid={`booking-awaiting-${b.id}`}
                title="Awaiting external venue confirmation — contact the venue and mark as confirmed once locked in"
                className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/50 text-amber-300 inline-flex items-center gap-1"
              >
                <AlertTriangle className="w-3 h-3" /> Awaiting
              </span>
            )}
            {b.confirmation_status === "confirmed" && (
              <span
                data-testid={`booking-venue-confirmed-${b.id}`}
                title="Venue has confirmed this booking"
                className="text-[10px] tracking-widest uppercase text-[#83B68B]"
              >
                · Venue-confirmed
              </span>
            )}
          </div>
          {/* Row-level checklist — quick visual on which bits are missing */}
          {b.status === "confirmed" && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`booking-checklist-${b.id}`}>
              {rowChecklist(b).map(item => (
                <span
                  key={item.key}
                  data-testid={`booking-checklist-${b.id}-${item.key}`}
                  title={item.ok ? `${item.label} — sorted` : `${item.label} — missing`}
                  className={`inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded ${
                    item.ok
                      ? "bg-[#83B68B]/15 border border-[#83B68B]/40 text-[#83B68B]"
                      : "bg-amber-500/10 border border-amber-400/50 text-amber-400"
                  }`}
                >
                  {item.ok ? <Check className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                  {item.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {canCancel && b.status === "confirmed" && (
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {!b.recurring_series_id && !outgoingByBooking.has(b.id) && !incomingByBooking.has(b.id) && (
            <button
              onClick={()=>openSwapDialog(b)}
              data-testid={`booking-swap-${b.id}`}
              className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-[#83B68B]/40 hover:bg-[#83B68B]/10 text-[#83B68B] rounded inline-flex items-center gap-1.5 transition-colors"
              title="Request a swap with another coach"
            >
              <ArrowLeftRight className="w-3 h-3" /> Swap
            </button>
          )}
          {!b.recurring_series_id && !outgoingByBooking.has(b.id) && !incomingByBooking.has(b.id) && !b.swap_open && (
            <button
              onClick={()=>openListDialog(b)}
              data-testid={`booking-list-swap-${b.id}`}
              className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B]/60 hover:text-[#83B68B] rounded inline-flex items-center gap-1.5 transition-colors"
              title="Flag this slot as swap-friendly on the Swap Board"
            >
              <Radio className="w-3 h-3" /> List for swap
            </button>
          )}
          {b.swap_open && (
            <button
              onClick={()=>unlist(b.id)}
              data-testid={`booking-unlist-swap-${b.id}`}
              className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-[#83B68B]/60 bg-[#83B68B]/10 text-[#83B68B] inline-flex items-center gap-1.5"
              title="Listed on the Swap Board — click to remove"
            >
              <Radio className="w-3 h-3" /> On board
            </button>
          )}
          {outgoingByBooking.has(b.id) && (
            <span data-testid={`booking-swap-pending-${b.id}`} className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-amber-400/40 bg-amber-400/10 text-amber-400 inline-flex items-center gap-1.5">
              <ArrowLeftRight className="w-3 h-3" /> Swap sent
            </span>
          )}
          {incomingByBooking.has(b.id) && (
            <span data-testid={`booking-swap-incoming-${b.id}`} className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-red-400/50 bg-red-400/10 text-red-400 inline-flex items-center gap-1.5 animate-pulse">
              <ArrowLeftRight className="w-3 h-3" /> Swap requested
            </span>
          )}
          {b.recurring_series_id && seriesCounts[b.recurring_series_id] > 1 && (
            <button
              onClick={()=>cancelSeries(b.recurring_series_id)}
              data-testid={`series-cancel-${b.recurring_series_id}`}
              className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-red-400 hover:text-red-500 rounded inline-flex items-center gap-1.5 transition-colors"
              title={`Cancel all ${seriesCounts[b.recurring_series_id]} remaining`}
            >
              <Repeat className="w-3 h-3" /> Cancel series ({seriesCounts[b.recurring_series_id]})
            </button>
          )}
          <button
            onClick={()=>setOpenId(b.id)}
            data-testid={`booking-amend-${b.id}`}
            className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5 transition-colors"
            title="View, amend or cancel this booking"
          >
            <Pencil className="w-3 h-3" /> Amend
          </button>
          <button
            onClick={()=>cancel(b.id)}
            data-testid={`booking-cancel-${b.id}`}
            className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-red-400 hover:text-red-500 rounded inline-flex items-center gap-1.5 transition-colors"
            title="Cancel this booking"
          >
            <X className="w-3 h-3" /> Cancel
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Your calendar</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 leading-tight">
            {isAdmin && showAllAdmin ? "All bookings" : "My bookings"}
          </h1>
          {isAdmin && showAllAdmin && (
            <p className="text-xs opacity-60 mt-1">Admin view — every booking in the club. Amend or cancel any of them.</p>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isAdmin && (
            <label className="flex items-center gap-2 text-[10px] tracking-widest uppercase opacity-80 select-none cursor-pointer px-3 py-2 rounded-md border border-border hover:border-[#83B68B]/60 transition-colors" data-testid="mybookings-admin-all-wrap">
              <input
                type="checkbox"
                checked={showAllAdmin}
                onChange={e => setShowAllAdmin(e.target.checked)}
                data-testid="mybookings-admin-all-toggle"
                className="w-4 h-4 accent-[#83B68B] cursor-pointer"
              />
              Show all bookings (admin)
            </label>
          )}
          <button
            onClick={exportCsv}
            data-testid="mybookings-export-csv"
            className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm"
            title="Download the currently-filtered bookings as a CSV (opens in Excel)"
          >
            <Download className="w-4 h-4" /> Export ({filtered.length})
          </button>
        </div>
      </div>

      <div className="card-tactical rounded-md p-5 mb-8" data-testid="mybookings-filters">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[10px] tracking-widest uppercase opacity-60 inline-flex items-center gap-2">
            <Filter className="w-3 h-3" /> Filters {activeFilterCount > 0 && <span className="text-[#83B68B]">· {activeFilterCount} active</span>}
          </div>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} data-testid="mybookings-clear-filters" className="text-[10px] tracking-widest uppercase opacity-60 hover:opacity-100 hover:text-red-400">Clear all</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2">
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Search</Label>
            <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Pitch, opponent, notes…" className="mt-1.5" data-testid="mybookings-search" />
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Session</Label>
            <Select value={sessionType} onValueChange={setSessionType}>
              <SelectTrigger className="mt-1.5" data-testid="mybookings-session"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="game">Game</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Year group</Label>
            <Select value={ageGroup} onValueChange={(v) => { setAgeGroup(v); if (v !== "all" && teamId !== "all") { const t = teamOptions.find(([id]) => id === teamId); if (t && t[1].age !== v) setTeamId("all"); } }}>
              <SelectTrigger className="mt-1.5" data-testid="mybookings-age-group"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All year groups</SelectItem>
                {ageOptions.map(a => <SelectItem key={a} value={a} data-testid={`mybookings-age-opt-${a}`}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Team</Label>
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger className="mt-1.5" data-testid="mybookings-team"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All teams</SelectItem>
                {teamOptions.filter(([, t]) => ageGroup === "all" || t.age === ageGroup).map(([id, t]) => <SelectItem key={id} value={id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Venue / pitch</Label>
            <Select value={venueFilter} onValueChange={setVenueFilter}>
              <SelectTrigger className="mt-1.5" data-testid="mybookings-venue"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All venues</SelectItem>
                {venueOptions.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="mt-1.5" data-testid="mybookings-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">From</Label>
            <div className="mt-1.5"><DatePickerUK value={dateFrom} onChange={setDateFrom} testId="mybookings-date-from" /></div>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">To</Label>
            <div className="mt-1.5"><DatePickerUK value={dateTo} onChange={setDateTo} testId="mybookings-date-to" /></div>
          </div>
        </div>
      </div>

      {loading ? <div className="opacity-60 font-display tracking-widest">LOADING…</div> : (
        <div className="space-y-10">
          {listedForSwap.length > 0 && (
            <section data-testid="mybookings-listed-for-swap" className="card-tactical rounded-md p-5 border-[#83B68B]/40">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] inline-flex items-center gap-2">
                    <Radio className="w-3 h-3" /> Your listings on the Swap Board
                  </div>
                  <div className="text-sm mt-1 opacity-80">{listedForSwap.length} of your upcoming bookings {listedForSwap.length === 1 ? "is" : "are"} open to swap.</div>
                </div>
                <Link to="/app/swap-board" data-testid="mybookings-open-swap-board" className="btn-volt text-[10px] tracking-widest uppercase px-4 py-2 rounded inline-flex items-center gap-1.5">
                  <ArrowLeftRight className="w-3 h-3" /> Open Swap Board
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                {listedForSwap.slice(0, 3).map(b => (
                  <div key={b.id} data-testid={`mybookings-listing-${b.id}`} className="rounded-md border border-[#83B68B]/30 bg-[#83B68B]/5 p-3 text-sm">
                    <div className="font-medium">{format(parseISO(b.booking_date), "EEE d MMM")} · {String(b.start_hour).padStart(2,"0")}:{String(b.start_min||0).padStart(2,"0")}</div>
                    <div className="text-xs opacity-70 mt-0.5">{pitchLabel(b)}{b.team_name ? ` · ${b.team_name}` : ""}</div>
                    {b.swap_open_note && <div className="text-xs opacity-70 italic mt-1">"{b.swap_open_note}"</div>}
                  </div>
                ))}
              </div>
            </section>
          )}
          {pendingCount > 0 && (
            <section data-testid="swap-requests-section" className="space-y-4">
              <div className="flex items-baseline gap-3">
                <h2 className="font-display font-bold text-2xl tracking-tight">Swap requests</h2>
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">{pendingCount} pending</span>
              </div>

              {swaps.incoming?.length > 0 && (
                <div className="space-y-2" data-testid="swap-incoming-list">
                  <div className="text-[10px] tracking-widest uppercase opacity-70">Requests waiting for your reply</div>
                  {swaps.incoming.map(s => <SwapRow key={s.id} s={s} mode="incoming" onAct={decideSwap} />)}
                </div>
              )}
              {swaps.outgoing?.length > 0 && (
                <div className="space-y-2" data-testid="swap-outgoing-list">
                  <div className="text-[10px] tracking-widest uppercase opacity-70">Requests you sent</div>
                  {swaps.outgoing.map(s => <SwapRow key={s.id} s={s} mode="outgoing" onAct={decideSwap} />)}
                </div>
              )}
              {isAdmin && swaps.admin_pending?.length > 0 && (
                <div className="space-y-2" data-testid="swap-admin-list">
                  <div className="text-[10px] tracking-widest uppercase opacity-70">Other members' pending swaps (admin view)</div>
                  {swaps.admin_pending.map(s => <SwapRow key={s.id} s={s} mode="admin" onAct={decideSwap} />)}
                </div>
              )}
            </section>
          )}

          <section>
            <h2 className="font-display font-bold text-2xl mb-4 tracking-tight">Upcoming ({upcoming.length})</h2>
            {upcoming.length === 0 ? (
              <div className="card-tactical rounded-md p-8 opacity-60 text-center">No upcoming bookings match these filters.</div>
            ) : (
              <div className="space-y-3" data-testid="upcoming-bookings">
                {upcoming.map(b => <Row key={b.id} b={b} canCancel />)}
              </div>
            )}
          </section>
          <section>
            <h2 className="font-display font-bold text-2xl mb-4 tracking-tight opacity-70">Past &amp; cancelled ({past.length})</h2>
            {past.length === 0 ? (
              <div className="opacity-50 text-sm">Nothing yet.</div>
            ) : (
              <div className="space-y-3 opacity-70">
                {past.map(b => <Row key={b.id} b={b} canCancel={false} />)}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ---- Swap request modal ---- */}
      <Dialog open={!!swapFor} onOpenChange={(v)=>{ if (!v) setSwapFor(null); }}>
        <DialogContent className="max-w-lg" data-testid="swap-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl inline-flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-[#83B68B]" /> Request a swap
            </DialogTitle>
            <DialogDescription className="text-xs opacity-70">
              Pick a booking to swap with. First accept (target coach or an admin) completes the swap — the time slot stays put, ownership + team + referee + notes move.
            </DialogDescription>
          </DialogHeader>
          {swapFor && (
            <div className="space-y-4">
              <div className="rounded-md border border-[#83B68B]/30 bg-[#83B68B]/5 p-3 text-sm">
                <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-1">Your booking</div>
                <div className="font-medium">{format(parseISO(swapFor.booking_date), "EEE d MMM")} · {String(swapFor.start_hour).padStart(2,"0")}:{String(swapFor.start_min||0).padStart(2,"0")} · {pitchLabel(swapFor)}</div>
                <div className="opacity-70 text-xs">{swapFor.team_name || "no team"}{swapFor.opponent ? ` vs ${swapFor.opponent}` : ""}</div>
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Choose a swap target</Label>
                {swapTargets.length === 0 ? (
                  <div className="mt-2 rounded-md border border-border p-4 text-sm opacity-60 text-center">
                    Nothing available to swap with today. Same-day bookings owned by other coaches or your team's next fixture will show up here.
                  </div>
                ) : (
                  <div className="mt-2 space-y-2 max-h-60 overflow-y-auto" data-testid="swap-target-list">
                    {swapTargets.map((t, idx) => {
                      const isBest = idx === 0 && t.match_score > 0 && !t.has_pending_swap;
                      return (
                      <label
                        key={t.id}
                        className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${swapTargetId === t.id ? "border-[#83B68B] bg-[#83B68B]/10" : "border-border hover:border-[#83B68B]/50"} ${t.has_pending_swap ? "opacity-40 pointer-events-none" : ""}`}
                        data-testid={`swap-target-${t.id}`}
                      >
                        <input
                          type="radio"
                          checked={swapTargetId === t.id}
                          onChange={()=>setSwapTargetId(t.id)}
                          disabled={t.has_pending_swap}
                          className="mt-1 accent-[#83B68B]"
                        />
                        <div className="flex-1 min-w-0 text-sm">
                          <div className="font-medium flex items-center gap-2 flex-wrap">
                            {format(parseISO(t.booking_date), "EEE d MMM")} · {String(t.start_hour).padStart(2,"0")}:{String(t.start_min||0).padStart(2,"0")} · {pitchLabel(t)}
                            {t.recurring_series_id && (
                              <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 inline-flex items-center gap-1 border border-amber-400/40" title="Part of a recurring series">
                                Series
                              </span>
                            )}
                            {isBest && (
                              <span data-testid={`swap-target-best-${t.id}`} className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-[#83B68B] text-white inline-flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> Best match
                              </span>
                            )}
                          </div>
                          <div className="text-xs opacity-70 mt-0.5">
                            {t.user_name}{t.team_name ? ` · ${t.team_name}` : ""}{t.opponent ? ` vs ${t.opponent}` : ""}
                            {t.session_type === "game" && <span className="ml-2 text-[10px] tracking-widest uppercase text-[#83B68B]">Game{t.match_type ? " · " + t.match_type : ""}</span>}
                          </div>
                          {t.match_reasons?.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {t.match_reasons.map((r, i) => (
                                <span key={`${r}-${i}`} className="text-[9px] tracking-wider px-1.5 py-0.5 rounded bg-[#83B68B]/15 text-[#83B68B]">{r}</span>
                              ))}
                            </div>
                          )}
                          {t.has_pending_swap && <div className="text-[10px] tracking-widest uppercase text-amber-400 mt-1">Already has a pending swap</div>}
                        </div>
                      </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Optional note</Label>
                <textarea
                  value={swapMessage}
                  onChange={e=>setSwapMessage(e.target.value)}
                  maxLength={500}
                  rows={3}
                  data-testid="swap-message"
                  className="w-full mt-2 bg-transparent border border-border rounded p-2 text-sm resize-none"
                  placeholder="Anything the other coach should know…"
                />
              </div>
              {/* Scope toggle — only shows when both sides are recurring series. */}
              {(() => {
                const chosen = swapTargets.find(t => t.id === swapTargetId);
                const bothSeries = !!swapFor?.recurring_series_id && !!chosen?.recurring_series_id;
                if (!bothSeries) return null;
                return (
                  <div className="rounded-md border border-amber-400/40 bg-amber-500/[0.04] p-3" data-testid="swap-scope-block">
                    <div className="text-[10px] tracking-widest uppercase text-amber-300 mb-2">Both bookings are recurring series</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label
                        className={`p-3 rounded border cursor-pointer text-xs ${swapScope === "single" ? "border-[#83B68B] bg-[#83B68B]/10" : "border-border hover:border-[#83B68B]/50"}`}
                        data-testid="swap-scope-single"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <input type="radio" checked={swapScope === "single"} onChange={()=>setSwapScope("single")} className="accent-[#83B68B]" />
                          <span className="font-semibold tracking-wide uppercase text-[10px]">Just this date</span>
                        </div>
                        <div className="opacity-70 pl-5">Swaps only the {swapFor && format(parseISO(swapFor.booking_date), "d MMM")} occurrence. Both dates detach from their series.</div>
                      </label>
                      <label
                        className={`p-3 rounded border cursor-pointer text-xs ${swapScope === "series" ? "border-[#83B68B] bg-[#83B68B]/10" : "border-border hover:border-[#83B68B]/50"}`}
                        data-testid="swap-scope-series"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <input type="radio" checked={swapScope === "series"} onChange={()=>setSwapScope("series")} className="accent-[#83B68B]" />
                          <span className="font-semibold tracking-wide uppercase text-[10px]">Every remaining date</span>
                        </div>
                        <div className="opacity-70 pl-5">Swaps every future date where both series line up on the same day. Series stays intact on both sides.</div>
                      </label>
                    </div>
                  </div>
                );
              })()}
              <div className="flex gap-2 pt-1">
                <button onClick={()=>setSwapFor(null)} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40">Cancel</button>
                <button
                  onClick={submitSwap}
                  disabled={swapBusy || !swapTargetId}
                  data-testid="swap-submit"
                  className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2"
                >
                  <ArrowLeftRight className="w-4 h-4" /> {swapBusy ? "Sending…" : "Request swap"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* ---- List for swap modal ---- */}
      <Dialog open={!!listFor} onOpenChange={(v)=>{ if (!v) setListFor(null); }}>
        <DialogContent className="max-w-md" data-testid="list-swap-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl inline-flex items-center gap-2">
              <Radio className="w-5 h-5 text-[#83B68B]" /> List for swap
            </DialogTitle>
            <DialogDescription className="text-xs opacity-70">
              Flags this booking as "happy to swap" on the Swap Board so other coaches can offer their slot in exchange. You'll still hold the booking until you accept an offer.
            </DialogDescription>
          </DialogHeader>
          {listFor && (
            <div className="space-y-4">
              <div className="rounded-md border border-[#83B68B]/30 bg-[#83B68B]/5 p-3 text-sm">
                <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-1">Listing</div>
                <div className="font-medium">{format(parseISO(listFor.booking_date), "EEE d MMM")} · {String(listFor.start_hour).padStart(2,"0")}:{String(listFor.start_min||0).padStart(2,"0")} · {pitchLabel(listFor)}</div>
                <div className="opacity-70 text-xs">{listFor.team_name || "no team"}{listFor.opponent ? ` vs ${listFor.opponent}` : ""}</div>
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Optional reason (visible to other coaches)</Label>
                <textarea
                  value={listNote}
                  onChange={e=>setListNote(e.target.value)}
                  maxLength={200}
                  rows={3}
                  data-testid="list-swap-note"
                  placeholder="e.g. Away that weekend, kids' birthday, prefer earlier kick-off…"
                  className="w-full mt-2 bg-transparent border border-border rounded p-2 text-sm resize-none"
                />
                <div className="text-[10px] opacity-50 mt-1">{listNote.length}/200</div>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={()=>setListFor(null)} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40">Cancel</button>
                <button
                  onClick={submitList}
                  disabled={listBusy}
                  data-testid="list-swap-submit"
                  className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2"
                >
                  <Radio className="w-4 h-4" /> {listBusy ? "Listing…" : "List on Swap Board"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Booking detail / amend / series edit modal (reused from calendar) ---- */}
      <BookingDetailDialog
        openId={openId} setOpenId={setOpenId}
        detail={detail} detailLoading={detailLoading}
        kitLabels={kitLabels} user={user}
        teams={teamsOwned} allTeams={allTeams}
        pitches={pitches} referees={referees}
        onCancel={async (id) => {
          if (!window.confirm("Cancel this booking?")) return;
          try {
            const b = detail;
            await api.delete(`/bookings/${id}`);
            toast.success("Booking cancelled");
            setOpenId(null); load();
            // Prompt to notify the Club Secretary for league/cup games only.
            if (b && b.session_type === "game" && (b.match_type === "league" || b.match_type === "cup") && secretaryEnabled) {
              setSecretaryNotice({ booking: b, kind: "cancel" });
            }
          } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Cancel failed"); }
        }}
        onAmend={async (id, patch) => {
          try {
            const b = detail;
            await api.put(`/bookings/${id}`, patch);
            toast.success("Booking updated");
            setOpenId(null); load();
            // Prompt to notify the Club Secretary when a league/cup game moved kick-off or date.
            const timeChanged = b && (patch.booking_date !== b.booking_date
              || patch.start_hour !== b.start_hour || (patch.start_min ?? 0) !== (b.start_min ?? 0));
            if (b && b.session_type === "game" && (b.match_type === "league" || b.match_type === "cup") && timeChanged && secretaryEnabled) {
              setSecretaryNotice({ booking: b, kind: "amend", patch });
            }
          } catch (err) {
            const d = err.response?.data?.detail;
            if (err.response?.status === 409 && d?.team_clash && user?.role === "admin") throw err;
            toast.error(formatApiError(d) || "Amend failed");
          }
        }}
        onSeriesPreview={async (seriesId, patch) => {
          try {
            const { data } = await api.post(`/bookings/series/${seriesId}/preview`, patch);
            return data;
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Preview failed");
            return null;
          }
        }}
        onSeriesAmend={async (seriesId, patch) => {
          try {
            const { data: res } = await api.patch(`/bookings/series/${seriesId}`, patch);
            toast.success(`Series updated · ${res.updated} future booking(s) changed`);
            setOpenId(null); load();
          } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Series amend failed"); }
        }}
        onSeriesCancel={async (seriesId) => {
          try {
            const { data: res } = await api.post(`/bookings/series/${seriesId}/cancel`);
            toast.success(`Series cancelled · ${res.cancelled} future booking(s) removed`);
            setOpenId(null); load();
          } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Cancel series failed"); }
        }}
        onNudgeReferee={async (id) => {
          if (!window.confirm("Email the booking coach to remind them to assign a referee?")) return;
          try {
            const { data: res } = await api.post(`/bookings/${id}/nudge-referee-coach`);
            toast.success(`Nudge sent to ${res.notified}`);
          } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Nudge failed"); }
        }}
      />
      <SecretaryNoticeDialog
        open={!!secretaryNotice}
        booking={secretaryNotice?.booking}
        kind={secretaryNotice?.kind}
        onClose={() => setSecretaryNotice(null)}
      />
      {/* Offer-swap dialog — reached via /app/bookings?swap_target=<id> from the
          New Booking clash panel. Target is pre-selected; the coach picks which
          of THEIR bookings to offer in exchange. */}
      <Dialog open={!!offerSwap} onOpenChange={(v)=>{ if (!v) setOfferSwap(null); }}>
        <DialogContent className="max-w-lg" data-testid="offer-swap-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl inline-flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-[#83B68B]" /> Propose swap
            </DialogTitle>
            <DialogDescription className="text-xs opacity-70">
              You wanted this slot but it's already booked. Pick one of your own bookings to offer in exchange — the current holder + admins will be notified.
            </DialogDescription>
          </DialogHeader>
          {offerSwap && (
            <div className="space-y-4">
              <div className="rounded-md border border-amber-400/40 bg-amber-500/[0.06] p-3 text-sm" data-testid="offer-swap-target">
                <div className="text-[10px] tracking-widest uppercase text-amber-300 mb-1">Slot you want</div>
                <div className="font-medium">{format(parseISO(offerSwap.target.booking_date), "EEE d MMM")} · {String(offerSwap.target.start_hour).padStart(2,"0")}:{String(offerSwap.target.start_min||0).padStart(2,"0")} · {pitchLabel(offerSwap.target)}</div>
                <div className="opacity-70 text-xs">{offerSwap.target.team_name || offerSwap.target.user_name}{offerSwap.target.opponent ? ` vs ${offerSwap.target.opponent}` : ""}</div>
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Pick one of your bookings to offer</Label>
                {(() => {
                  const mine = bookings.filter(b =>
                    b.status === "confirmed"
                    && b.user_id === user?.id
                    && b.id !== offerSwap.target.id
                    && parseISO(b.booking_date) >= new Date(new Date().toDateString())
                  );
                  if (mine.length === 0) return (
                    <div className="mt-2 rounded-md border border-border p-4 text-sm opacity-60 text-center" data-testid="offer-swap-empty">
                      You don't have any upcoming bookings to offer.
                    </div>
                  );
                  return (
                    <div className="mt-2 space-y-2 max-h-60 overflow-y-auto" data-testid="offer-swap-source-list">
                      {mine.map(b => (
                        <label
                          key={b.id}
                          className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${offerSourceId === b.id ? "border-[#83B68B] bg-[#83B68B]/10" : "border-border hover:border-[#83B68B]/50"}`}
                          data-testid={`offer-swap-source-${b.id}`}
                        >
                          <input type="radio" checked={offerSourceId === b.id} onChange={()=>setOfferSourceId(b.id)} className="mt-1 accent-[#83B68B]" />
                          <div className="flex-1 min-w-0 text-sm">
                            <div className="font-medium">{format(parseISO(b.booking_date), "EEE d MMM")} · {String(b.start_hour).padStart(2,"0")}:{String(b.start_min||0).padStart(2,"0")} · {pitchLabel(b)}</div>
                            <div className="text-xs opacity-70 mt-0.5">{b.team_name || "no team"}{b.opponent ? ` vs ${b.opponent}` : ""}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Optional note</Label>
                <textarea
                  value={offerMessage}
                  onChange={e=>setOfferMessage(e.target.value)}
                  maxLength={500}
                  rows={3}
                  data-testid="offer-swap-message"
                  className="w-full mt-2 bg-transparent border border-border rounded p-2 text-sm resize-none"
                  placeholder="Anything the current holder should know…"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={()=>setOfferSwap(null)} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40">Cancel</button>
                <button
                  onClick={submitOfferSwap}
                  disabled={offerBusy || !offerSourceId}
                  data-testid="offer-swap-submit"
                  className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2"
                >
                  <ArrowLeftRight className="w-4 h-4" /> {offerBusy ? "Sending…" : "Propose swap"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SwapRow({ s, mode, onAct }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const src = s.initiator_booking, tgt = s.target_booking;
  const canDecide = isAdmin || user?.id === s.target_user_id;
  const canCancel = user?.id === s.initiator_user_id || isAdmin;
  const slot = (b) => b
    ? `${format(parseISO(b.booking_date), "EEE d MMM")} · ${String(b.start_hour).padStart(2,"0")}:${String(b.start_min||0).padStart(2,"0")} · ${pitchLabel(b)}`
    : "(booking removed)";
  return (
    <div className="card-tactical rounded-md p-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`swap-request-${s.id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-xs opacity-70 mb-1">
          <span className="text-[#83B68B] font-medium">{s.initiator_name}</span>
          <ArrowLeftRight className="w-3 h-3 inline mx-2 opacity-60" />
          <span className="text-[#83B68B] font-medium">{s.target_name}</span>
        </div>
        <div className="text-sm">
          <div><span className="opacity-60 text-[10px] tracking-widest uppercase mr-1">A</span> {slot(src)}</div>
          <div><span className="opacity-60 text-[10px] tracking-widest uppercase mr-1">B</span> {slot(tgt)}</div>
        </div>
        {s.message && (
          <div className="text-xs opacity-70 mt-2 inline-flex items-start gap-1.5">
            <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" /> {s.message}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {mode === "incoming" && canDecide && (
          <>
            <button onClick={()=>onAct(s.id, "accept")} data-testid={`swap-accept-${s.id}`} className="btn-volt px-4 py-2 rounded-md text-xs tracking-widest uppercase inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Accept
            </button>
            <button onClick={()=>onAct(s.id, "decline")} data-testid={`swap-decline-${s.id}`} className="px-4 py-2 rounded-md text-xs tracking-widest uppercase border border-red-500/40 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5">
              <Ban className="w-3.5 h-3.5" /> Decline
            </button>
          </>
        )}
        {mode === "outgoing" && canCancel && (
          <button onClick={()=>onAct(s.id, "cancel")} data-testid={`swap-cancel-${s.id}`} className="px-4 py-2 rounded-md text-xs tracking-widest uppercase border border-border hover:border-red-500/60 hover:text-red-400 inline-flex items-center gap-1.5">
            <X className="w-3.5 h-3.5" /> Cancel request
          </button>
        )}
        {mode === "admin" && (
          <>
            <button onClick={()=>onAct(s.id, "accept")} data-testid={`swap-admin-accept-${s.id}`} className="btn-volt px-4 py-2 rounded-md text-xs tracking-widest uppercase inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Approve
            </button>
            <button onClick={()=>onAct(s.id, "decline")} data-testid={`swap-admin-decline-${s.id}`} className="px-4 py-2 rounded-md text-xs tracking-widest uppercase border border-red-500/40 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5">
              <Ban className="w-3.5 h-3.5" /> Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}
